import React from "react";

/**
 * StrainScan Pro themed vector logo.
 *
 * 100% SVG — no raster, transparent background, integrates seamlessly with
 * the animated theme. Three variants:
 *   variant="full" → Leaf-in-HUD-scanner + STRAINSCAN PRO wordmark + Medical Care badge
 *   variant="stack" → Same as full but sized smaller (used on About/Solutions)
 *   variant="mark" → Just the leaf-in-scanner icon
 *   variant="wordmark" → Just the STRAINSCAN PRO wordmark (used in navbar)
 *
 * The HUD ring rotates slowly and the highlight arc pulses to feel alive.
 */

function ScannerRing({ animated = true, size = 320 }) {
  const ticks = 60;
  return (
    <svg
      viewBox="0 0 220 220"
      width={size}
      height={size}
      role="img"
      aria-label="StrainScan HUD"
      style={{ display: "block" }}
    >
      <defs>
        <radialGradient id="ring-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="80%" stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(157,252,4,0.35)" />
        </radialGradient>
        <linearGradient id="hl-arc" x1="0" y1="0" x2="1" y2="0.5">
          <stop offset="0%" stopColor="#9dfc04" stopOpacity="0" />
          <stop offset="50%" stopColor="#d0ff62" stopOpacity="1" />
          <stop offset="100%" stopColor="#9dfc04" stopOpacity="0" />
        </linearGradient>
        <filter id="hl-blur" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.6" />
        </filter>
      </defs>

      {/* Outer faint glow */}
      <circle cx="110" cy="110" r="108" fill="url(#ring-glow)" />

      {/* Outer thin ring */}
      <circle cx="110" cy="110" r="100" fill="none" stroke="rgba(157,252,4,0.25)" strokeWidth="1" />

      {/* Tick marks ring (rotating) */}
      <g
        style={
          animated
            ? { transformOrigin: "110px 110px", animation: "ss-rotate 22s linear infinite" }
            : undefined
        }
      >
        {Array.from({ length: ticks }).map((_, i) => {
          const a = (i / ticks) * Math.PI * 2;
          const r1 = 96;
          const r2 = i % 5 === 0 ? 86 : 91;
          const x1 = 110 + Math.cos(a) * r1;
          const y1 = 110 + Math.sin(a) * r1;
          const x2 = 110 + Math.cos(a) * r2;
          const y2 = 110 + Math.sin(a) * r2;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="rgba(157,252,4,0.7)"
              strokeWidth={i % 5 === 0 ? 1.4 : 0.8}
            />
          );
        })}
      </g>

      {/* Status segments (the white blocks in the source artwork) */}
      <g>
        {[15, 30, 60, 200, 220, 250, 280, 300, 330].map((deg, i) => {
          const a = (deg * Math.PI) / 180;
          const x = 110 + Math.cos(a) * 92;
          const y = 110 + Math.sin(a) * 92;
          return (
            <rect
              key={i}
              x={x - 4}
              y={y - 1}
              width="8"
              height="2"
              fill="#ffffff"
              opacity="0.85"
              transform={`rotate(${deg + 90} ${x} ${y})`}
            />
          );
        })}
      </g>

      {/* Bright highlight arc (top-right) */}
      <g
        style={
          animated
            ? { transformOrigin: "110px 110px", animation: "ss-arc-pulse 4.8s ease-in-out infinite" }
            : undefined
        }
      >
        <path
          d="M 110 12 A 98 98 0 0 1 208 110"
          stroke="url(#hl-arc)"
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          filter="url(#hl-blur)"
        />
        <path
          d="M 110 12 A 98 98 0 0 1 208 110"
          stroke="#d6ff7a"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
        />
      </g>

      {/* Inner grid (subtle) inside the lens */}
      <defs>
        <clipPath id="lens">
          <circle cx="110" cy="110" r="80" />
        </clipPath>
      </defs>
      <g clipPath="url(#lens)" opacity="0.35">
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`g-${i}`}
            x1={30 + i * 20}
            y1="30"
            x2={30 + i * 20}
            y2="190"
            stroke="rgba(157,252,4,0.4)"
            strokeWidth="0.6"
          />
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`g2-${i}`}
            x1="30"
            y1={30 + i * 20}
            x2="190"
            y2={30 + i * 20}
            stroke="rgba(157,252,4,0.4)"
            strokeWidth="0.6"
          />
        ))}
      </g>

      {/* Inner ring */}
      <circle cx="110" cy="110" r="80" fill="none" stroke="rgba(157,252,4,0.3)" strokeWidth="1" />
    </svg>
  );
}

function SproutLogoMark({ size = 200 }) {
  /* Agricultural sprout — two mirrored ovoid leaves branching from a
   * central curved stem. Universal "agriculture / growth" mark that reads
   * clearly at every size while keeping the same green scanner palette. */
  const cx = 100;
  const baseY = 172;

  // Central stem: a soft S-curve going from base up to the sprout tip.
  const stemPath = `M ${cx} ${baseY} C ${cx - 3} ${baseY - 30}, ${cx + 3} ${baseY - 65}, ${cx} ${baseY - 100}`;

  // Ovoid leaf path — asymmetric teardrop, tip at top, base at bottom.
  //   L = leaf length, W = max width, curve = how much it bows outward.
  const leafPath = (L, W, curve = 1) => {
    const midY = -L * 0.5;
    return [
      "M 0 0",
      // Outer side curves out to the tip
      `C ${W * curve * 0.9} ${midY * 0.35}, ${W * curve * 0.95} ${midY * 1.55}, 0 ${-L}`,
      // Inner side curves back to the base
      `C ${-W * 0.25} ${midY * 1.55}, ${-W * 0.28} ${midY * 0.35}, 0 0`,
      "Z",
    ].join(" ");
  };

  // Vein layout inside a leaf
  const veins = (L, W) => {
    const out = [];
    const count = 4;
    for (let i = 1; i <= count; i++) {
      const t = 0.15 + (i / (count + 1)) * 0.7;
      const y = -L * t;
      const w = Math.sin(t * Math.PI) * W * 0.6;
      out.push(
        <line
          key={i}
          x1="0"
          y1={y}
          x2={w}
          y2={y - L * 0.06}
          stroke="rgba(58,115,0,0.7)"
          strokeWidth="0.6"
        />,
      );
    }
    return out;
  };

  const leafL = 92;
  const leafW = 34;

  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      role="img"
      aria-label="Agricultural sprout"
      style={{ display: "block", filter: "drop-shadow(0 0 14px rgba(157,252,4,0.5))" }}
    >
      <defs>
        <linearGradient id="leaf-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bdf72a" />
          <stop offset="55%" stopColor="#5cb800" />
          <stop offset="100%" stopColor="#2a5500" />
        </linearGradient>
        <linearGradient id="leaf-vein" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f1ffb4" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#3e7900" stopOpacity="0.8" />
        </linearGradient>
        <linearGradient id="stem-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5cb800" />
          <stop offset="100%" stopColor="#2a5500" />
        </linearGradient>
      </defs>

      {/* Central stem (curves gently up) */}
      <path
        d={stemPath}
        fill="none"
        stroke="url(#stem-grad)"
        strokeWidth="3.4"
        strokeLinecap="round"
      />
      <path
        d={stemPath}
        fill="none"
        stroke="#bdf72a"
        strokeWidth="1"
        strokeLinecap="round"
        opacity="0.55"
      />

      {/* Right leaf */}
      <g transform={`translate(${cx} ${baseY - 55}) rotate(45)`}>
        <path
          d={leafPath(leafL, leafW, 1)}
          fill="url(#leaf-fill)"
          stroke="#1f4500"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        {/* Center vein */}
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={-leafL + 4}
          stroke="url(#leaf-vein)"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {veins(leafL, leafW)}
      </g>

      {/* Left leaf (mirrored) */}
      <g transform={`translate(${cx} ${baseY - 55}) rotate(-45) scale(-1, 1)`}>
        <path
          d={leafPath(leafL, leafW, 1)}
          fill="url(#leaf-fill)"
          stroke="#1f4500"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={-leafL + 4}
          stroke="url(#leaf-vein)"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {veins(leafL, leafW)}
      </g>

      {/* Center node where leaves meet the stem */}
      <circle cx={cx} cy={baseY - 55} r="3" fill="#9dfc04" />
      <circle cx={cx} cy={baseY - 55} r="6" fill="rgba(157,252,4,0.3)" />
    </svg>
  );
}

function Wordmark({ size = "lg" }) {
  // Sizes map to [strain font-size, pro font-size] using clamp() to scale
  // responsively. Mobile minimums are deliberately small so the wordmark
  // never overflows narrow phones — the vw component takes over fast as
  // the screen widens, hitting the desktop max around ~1280px.
  const sizes = {
    sm: ["clamp(14px, 3.6vw, 22px)", "clamp(7px, 1.8vw, 11px)"],
    md: ["clamp(20px, 5vw, 44px)", "clamp(10px, 2.5vw, 22px)"],
    lg: ["clamp(22px, 5.4vw, 68px)", "clamp(11px, 2.7vw, 34px)"],
    xl: ["clamp(26px, 6.2vw, 88px)", "clamp(13px, 3.1vw, 44px)"],
  };
  const [s1, s2] = sizes[size] || sizes.lg;
  const proLineW = `2.6em`;

  // Inline SVG companion lets the brand kit serialize the wordmark to a real
  // SVG file with embedded vector text. Pixel sizes are picked to roughly
  // match the visual layout of the HTML version above.
  const svgSizes = {
    sm: { strain: 22, pro: 11, w: 220, h: 56 },
    md: { strain: 44, pro: 22, w: 440, h: 112 },
    lg: { strain: 88, pro: 44, w: 880, h: 224 },
    xl: { strain: 128, pro: 64, w: 1280, h: 320 },
  };
  const sv = svgSizes[size] || svgSizes.lg;

  return (
    <div
      className="flex flex-col items-center max-w-full"
      style={{ lineHeight: 1, overflow: "hidden", position: "relative" }}
    >
      {/* Visible HTML wordmark — used for live page render (text-shadow glow) */}
      <div
        className="font-display font-black"
        style={{
          fontSize: s1,
          letterSpacing: "0.05em",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #d4d4d4 50%, #8a8a8a 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            paddingRight: 2,
          }}
        >
          STRAIN
        </span>
        <span
          style={{
            color: "var(--ss-green)",
            textShadow: "0 0 14px rgba(157,252,4,0.65), 0 0 30px rgba(157,252,4,0.35)",
          }}
        >
          SCAN
        </span>
        <sup
          style={{
            color: "var(--ss-green)",
            fontSize: "0.18em",
            marginLeft: 3,
            verticalAlign: "super",
          }}
        >
          ™
        </sup>
      </div>

      <div
        className="flex items-center mt-2"
        style={{ gap: "0.32em", fontSize: s2 }}
      >
        <span
          style={{
            display: "block",
            width: proLineW,
            height: 2,
            background: "var(--ss-green)",
            boxShadow: "0 0 8px rgba(157,252,4,0.7)",
          }}
        />
        <span
          className="font-display font-black"
          style={{
            color: "var(--ss-green)",
            fontSize: "1em",
            letterSpacing: "0.32em",
            textShadow: "0 0 14px rgba(157,252,4,0.55)",
            paddingLeft: "0.32em",
            lineHeight: 1,
          }}
        >
          PRO
        </span>
        <span
          style={{
            display: "block",
            width: proLineW,
            height: 2,
            background: "var(--ss-green)",
            boxShadow: "0 0 8px rgba(157,252,4,0.7)",
          }}
        />
      </div>

      {/* Hidden SVG twin — picked up by the brand-kit serializer for export */}
      <svg
        viewBox={`0 0 ${sv.w} ${sv.h}`}
        width={sv.w}
        height={sv.h}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
        data-export="wordmark"
      >
        <defs>
          <linearGradient id={`wm-strain-g-${size}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="50%" stopColor="#d4d4d4" />
            <stop offset="100%" stopColor="#8a8a8a" />
          </linearGradient>
          <filter id={`wm-glow-${size}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" />
          </filter>
        </defs>
        <text
          x={sv.w / 2}
          y={sv.strain * 0.92}
          textAnchor="middle"
          fontFamily="Orbitron, 'Rajdhani', sans-serif"
          fontWeight="900"
          fontSize={sv.strain}
          letterSpacing="0.05em"
        >
          <tspan fill={`url(#wm-strain-g-${size})`}>STRAIN</tspan>
          <tspan fill="#9DFC04" filter={`url(#wm-glow-${size})`}>SCAN</tspan>
          <tspan fill="#9DFC04" fontSize={sv.strain * 0.18} dy={-sv.strain * 0.55}>™</tspan>
        </text>
        {/* — PRO — */}
        <line
          x1={sv.w * 0.18}
          y1={sv.strain * 1.6}
          x2={sv.w * 0.18 + sv.pro * 2.6}
          y2={sv.strain * 1.6}
          stroke="#9DFC04"
          strokeWidth="2"
        />
        <text
          x={sv.w / 2}
          y={sv.strain * 1.65}
          textAnchor="middle"
          fontFamily="Orbitron, 'Rajdhani', sans-serif"
          fontWeight="900"
          fontSize={sv.pro}
          letterSpacing="0.32em"
          fill="#9DFC04"
        >
          PRO
        </text>
        <line
          x1={sv.w * 0.82 - sv.pro * 2.6}
          y1={sv.strain * 1.6}
          x2={sv.w * 0.82}
          y2={sv.strain * 1.6}
          stroke="#9DFC04"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}

function MedicalCareSubBadge({ size = "md" }) {
  const fontMain = size === "sm" ? 13 : 18;
  const fontSub = size === "sm" ? 7 : 9;
  return (
    <div className="flex items-center gap-3" data-testid="medical-care-badge">
      {/* Tech-head silhouette in purple gradient */}
      <svg width={size === "sm" ? 36 : 50} height={size === "sm" ? 36 : 50} viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="mc-gx" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#c86bff" />
            <stop offset="55%" stopColor="#9a4cff" />
            <stop offset="100%" stopColor="#ff3bb7" />
          </linearGradient>
        </defs>
        <g stroke="url(#mc-gx)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 30c0-10 8-18 18-18 6 0 11 3 14 7 3 4 4 9 4 14v5c0 4-1 6-4 8-3 2-5 5-5 9v3H22v-4c0-3-1-5-3-7-3-2-5-5-5-9v-8z" />
          <path d="M22 32h6M22 26h4M22 38h5M34 22v6h6M34 30h5M34 36h7" />
          <circle cx="28" cy="32" r="1.6" fill="url(#mc-gx)" />
          <circle cx="40" cy="28" r="1.6" fill="url(#mc-gx)" />
          <circle cx="41" cy="36" r="1.6" fill="url(#mc-gx)" />
          <circle cx="27" cy="26" r="1.2" fill="url(#mc-gx)" />
          <path d="M10 22h4M10 30h4M10 38h4M50 22h4M50 30h4M50 38h4" />
        </g>
      </svg>
      <div className="flex flex-col leading-none">
        <div
          className="font-display font-bold"
          style={{
            fontSize: fontSub,
            letterSpacing: "0.4em",
            background: "linear-gradient(90deg,#c86bff,#ff3bb7)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginBottom: 4,
          }}
        >
          POWERED BY
        </div>
        <div
          className="font-display font-black"
          style={{
            fontSize: fontMain,
            letterSpacing: "0.18em",
            color: "#ffffff",
          }}
        >
          MEDICAL CARE<sup style={{ fontSize: fontSub * 0.8, verticalAlign: "super" }}>®</sup>
        </div>
        <div
          className="font-display font-bold mt-1"
          style={{
            fontSize: fontSub,
            letterSpacing: "0.5em",
            background: "linear-gradient(90deg,#c86bff,#ff3bb7)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          TECHNOLOGIES INC.
        </div>
      </div>
    </div>
  );
}

export default function StrainScanLogo({ variant = "wordmark", className = "", animated = true, textSize = "sm" }) {
  if (variant === "wordmark") {
    // Allow size override via className-style prop
    return (
      <div className={`inline-flex items-center max-w-full ${className}`} data-testid="brand-logo" style={{ overflow: "hidden" }}>
        <Wordmark size={textSize} />
      </div>
    );
  }

  if (variant === "mark") {
    return (
      <div className={`relative inline-block ${className}`} data-testid="brand-logo" style={{ width: 220, height: 220 }}>
        <ScannerRing animated={animated} size="100%" />
        <div className="absolute inset-[18%] flex items-center justify-center">
          <SproutLogoMark size="100%" />
        </div>
      </div>
    );
  }

  // full + stack — use clamp()-based responsive sizing so the logo scales
  // gracefully on small viewports without overflowing.
  const ringSize =
    variant === "stack"
      ? "clamp(120px, 20vw, 200px)"
      : "clamp(160px, 24vw, 260px)";

  return (
    <div
      className={`flex flex-col items-center w-full max-w-full ${className}`}
      data-testid="brand-logo"
    >
      {/* Leaf in scanner */}
      <div
        className="relative"
        style={{ width: ringSize, height: ringSize, maxWidth: "100%" }}
      >
        <ScannerRing animated={animated} size="100%" />
        <div className="absolute inset-[18%] flex items-center justify-center">
          <SproutLogoMark size="100%" />
        </div>
      </div>

      {/* Wordmark */}
      <div className="mt-6 px-2 w-full flex justify-center">
        <Wordmark size={variant === "stack" ? "lg" : "xl"} />
      </div>
    </div>
  );
}
